/**
 * Wires browser errors into the backend Cloud Error Reporting stream.
 *
 * Two sources funnel here:
 *   1. The namespaced logger's `setErrorReporter` hook — every `log.warn` /
 *      `log.error` (incl. the React error boundaries in app/error.tsx +
 *      global-error.tsx).
 *   2. Global `window.onerror` / `unhandledrejection` — uncaught exceptions and
 *      rejected promises that never reach a React boundary (event handlers,
 *      async callbacks). This is the actual blind spot: those were console-only
 *      before, invisible in prod.
 *
 * Everything is best-effort and silent on failure — a reporter that throws or
 * logs would risk a feedback loop, so failures are swallowed without re-logging.
 */

import { API, authHeaders } from "@/lib/api";
import { setErrorReporter, type LogEntry } from "@/lib/log";

const ENDPOINT = `${API}/api/client-errors`;

// Client-side caps mirroring the backend ClientError model (routes/
// client_errors.py). The backend 422s on overflow and this fetch ignores the
// status, so an un-capped long minified stack would be silently dropped —
// truncate here so the report still lands.
const _MAX_MESSAGE = 4000;
const _MAX_TAG = 120;
const _MAX_URL = 2000;

// Throttle so a render loop or a flapping request can't flood the endpoint.
// A short dedupe window collapses identical repeated errors; a per-session hard
// cap bounds total volume.
const _DEDUPE_WINDOW_MS = 10_000;
const _SESSION_CAP = 100;
const _recent = new Map<string, number>();
let _sent = 0;
let _installed = false;

function _shouldSend(signature: string): boolean {
  if (_sent >= _SESSION_CAP) return false;
  const now = Date.now();
  const last = _recent.get(signature);
  if (last !== undefined && now - last < _DEDUPE_WINDOW_MS) return false;
  _recent.set(signature, now);
  // Keep the dedupe map from growing unbounded over a long-lived session.
  if (_recent.size > 200) {
    for (const [k, t] of _recent) {
      if (now - t > _DEDUPE_WINDOW_MS) _recent.delete(k);
    }
  }
  return true;
}

// Token-bearing routes use a URL path segment as an auth/invite credential —
// frontend routes (/share, /invite, /workspace-invite) and the API paths they
// fetch (/api/share, /api/invites, /api/workspace-invites, /api/org/invites,
// and the workspace-scoped /api/workspaces/{id}/invites/{token}). Redact the
// token segment wherever it appears (location.href, stack traces, or structured
// log data like requestOk's `{ url }`) so a live link never lands in Error
// Reporting.
const _TOKEN_PATH_RE =
  /\/(api\/share|api\/invites|api\/workspace-invites|api\/org\/invites|share|invite|workspace-invite)\/[^/\s"')\]]+/g;
const _WS_INVITE_PATH_RE = /(\/api\/workspaces\/[^/]+\/invites)\/[^/\s"')\]]+/g;
// Generic safety net: any long opaque path segment (22+ url-safe chars — the
// length of a `secrets.token_urlsafe(16)` share token, the shortest bearer
// token the backend mints) is redacted regardless of route, so a new
// token-bearing route is covered without enumerating it here. No
// trailing-delimiter anchor: the char class ends the run at any non-token byte,
// so a token is scrubbed whether it's followed by `/`, a quote, comma, paren,
// or whitespace (e.g. embedded in `{"url":"/api/foo/<token>"}`). Short ids
// (task ids, workspace slugs) fall below the threshold and survive; the
// explicit rules above catch shorter known tokens.
const _OPAQUE_SEGMENT_RE = /(\/)[A-Za-z0-9_-]{22,}/g;

function _redactTokens(s: string): string {
  return s
    .replace(_TOKEN_PATH_RE, "/$1/[redacted]")
    .replace(_WS_INVITE_PATH_RE, "$1/[redacted]")
    .replace(_OPAQUE_SEGMENT_RE, "$1[redacted]");
}

function _redactUrl(href: string): string {
  try {
    const u = new URL(href);
    u.pathname = _redactTokens(u.pathname);
    // Snapshot before mutating. Scrub explicit token params, and any param
    // whose (decoded) value embeds a tokenized route — e.g.
    // /login?next=%2Finvite%2F<token>.
    for (const [key, value] of [...u.searchParams.entries()]) {
      if (["token", "access_token", "id_token"].includes(key)) {
        u.searchParams.set(key, "[redacted]");
        continue;
      }
      const redacted = _redactTokens(value);
      if (redacted !== value) u.searchParams.set(key, redacted);
    }
    return `${u.origin}${u.pathname}${u.search}`;
  } catch {
    return href;
  }
}

async function _post(body: {
  message: string;
  tag: string;
  level: string;
}): Promise<void> {
  const signature = `${body.tag}::${body.message}`.slice(0, 300);
  if (!_shouldSend(signature)) return;
  _sent += 1;
  try {
    // The sink is intentionally unauthenticated (an error can fire before auth
    // resolves, or *because* it failed). authHeaders awaits getIdToken(), which
    // can reject on an auth-failure path — fall back to minimal headers so those
    // reports still reach the endpoint instead of being swallowed.
    let headers: Headers;
    try {
      headers = await authHeaders(ENDPOINT, {
        headers: { "Content-Type": "application/json" },
      });
    } catch {
      headers = new Headers({ "Content-Type": "application/json" });
    }
    const rawUrl =
      typeof location !== "undefined" ? _redactUrl(location.href) : null;
    await fetch(ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({
        message: _redactTokens(body.message).slice(0, _MAX_MESSAGE),
        tag: body.tag.slice(0, _MAX_TAG),
        level: body.level,
        url: rawUrl ? rawUrl.slice(0, _MAX_URL) : null,
        user_agent:
          typeof navigator !== "undefined"
            ? navigator.userAgent.slice(0, _MAX_URL)
            : null,
      }),
      // Survive a page navigation/unload (relevant for the unload-time errors).
      keepalive: true,
    });
  } catch {
    // Reporting must never surface — swallow to avoid a feedback loop.
  }
}

// `Error` has non-enumerable `message`/`stack`, so a plain JSON.stringify of a
// caught exception logged as `{ error: e }` collapses to `{}`. Several callers
// log that shape — serialize nested Errors explicitly so the exception text
// actually reaches Error Reporting.
function _errorReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  return value;
}

// A handled client-side API failure (4xx) logged at warn level — these are
// expected user/input conditions (e.g. a revoked share/invite preview the page
// renders as "unavailable"), not bugs. Drop them so they don't pollute the
// error-triage stream. 5xx and non-HTTP warns still flow. The status may be on
// `data` itself (requestOk's `{ url, method, status, detail }`) OR on a nested
// caught `ApiError` logged as `{ error: e }` / `{ err: e }`.
function _statusOf(v: unknown): number | undefined {
  if (typeof v !== "object" || v === null) return undefined;
  const s = (v as { status?: unknown }).status;
  return typeof s === "number" ? s : undefined;
}

function _isHandledClientFailure(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return false;
  const d = data as { error?: unknown; err?: unknown };
  for (const candidate of [data, d.error, d.err]) {
    const status = _statusOf(candidate);
    if (status !== undefined) return status >= 400 && status < 500;
  }
  return false;
}

function _fromLogEntry(entry: LogEntry): void {
  // Only warn/error reach the reporter (enforced in log.ts), but guard anyway.
  if (entry.level !== "warn" && entry.level !== "error") return;
  if (entry.level === "warn" && _isHandledClientFailure(entry.data)) return;
  let message = entry.message;
  if (entry.data instanceof Error) {
    message = `${message}: ${entry.data.message}\n${entry.data.stack ?? ""}`;
  } else if (entry.data !== undefined) {
    try {
      message = `${message} ${JSON.stringify(entry.data, _errorReplacer)}`;
    } catch {
      // non-serializable data — message alone is fine.
    }
  }
  void _post({ message, tag: entry.tag, level: entry.level });
}

function _onError(event: ErrorEvent): void {
  const err = event.error;
  const message =
    err instanceof Error
      ? `${err.message}\n${err.stack ?? ""}`
      : event.message || "uncaught error";
  void _post({ message, tag: "window.onerror", level: "error" });
}

function _onRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  const message =
    reason instanceof Error
      ? `${reason.message}\n${reason.stack ?? ""}`
      : String(reason);
  void _post({ message, tag: "unhandledrejection", level: "error" });
}

/** Install the logger hook + global listeners. Idempotent. */
export function installErrorReporter(): void {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  setErrorReporter(_fromLogEntry);
  window.addEventListener("error", _onError);
  window.addEventListener("unhandledrejection", _onRejection);
}
